import React, { useState, useEffect, useRef } from 'react';

const GuestSelector = ({ rooms, onRoomsChange }) => {
  const [showPopup, setShowPopup] = useState(false);
  const guestInputRef = useRef(null);

  // Handle window resize and scroll to reposition dropdown
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (showPopup && guestInputRef.current) {
        const dropdown = document.querySelector('.guest-selector-popup');
        if (dropdown) {
          const rect = guestInputRef.current.getBoundingClientRect();
          const newTop = rect.bottom + 8;
          const newLeft = rect.left;
          const newWidth = Math.max(rect.width, 320);
          
          dropdown.style.top = `${newTop}px`;
          dropdown.style.left = `${newLeft}px`;
          dropdown.style.width = `${newWidth}px`;
        }
      }
    };

    if (showPopup) {
      // Update position immediately
      updateDropdownPosition();
      
      // Add event listeners for continuous updates
      window.addEventListener('scroll', updateDropdownPosition, { passive: true });
      window.addEventListener('resize', updateDropdownPosition, { passive: true });
      document.addEventListener('scroll', updateDropdownPosition, { passive: true, capture: true });
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
        document.removeEventListener('scroll', updateDropdownPosition, { capture: true });
      };
    }
  }, [showPopup]);

  const getGuestSummary = () => {
    const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0);
    const totalChildren = rooms.reduce((sum, room) => sum + room.children, 0);
    const totalRooms = rooms.length;
    
    return `${totalAdults} Adult${totalAdults > 1 ? 's' : ''}, ${totalChildren} Child${totalChildren !== 1 ? 'ren' : ''}, ${totalRooms} Room${totalRooms > 1 ? 's' : ''}`;
  };

  const updateRoomCount = (newCount) => {
    const count = Math.max(1, Math.min(6, newCount));
    const newRooms = [...rooms];
    
    if (count > newRooms.length) {
      for (let i = newRooms.length; i < count; i++) {
        newRooms.push({
          roomNumber: i + 1,
          adults: 2,
          children: 0,
          childAges: []
        });
      }
    } else if (count < newRooms.length) {
      newRooms.splice(count);
    }
    
    newRooms.forEach((room, index) => {
      room.roomNumber = index + 1;
    });
    
    onRoomsChange(newRooms);
  };

  const updateRoom = (roomIndex, field, value) => {
    const newRooms = [...rooms];
    
    if (field === 'adults') {
      newRooms[roomIndex].adults = Math.max(1, Math.min(8, value));
    } else if (field === 'children') {
      const childCount = Math.max(0, Math.min(4, value));
      newRooms[roomIndex].children = childCount;
      
      const childAges = [...(newRooms[roomIndex].childAges || [])];
      if (childCount > childAges.length) {
        for (let i = childAges.length; i < childCount; i++) {
          childAges.push(1);
        }
      } else if (childCount < childAges.length) {
        childAges.splice(childCount);
      }
      newRooms[roomIndex].childAges = childAges;
    }
    
    onRoomsChange(newRooms);
  };

  const updateChildAge = (roomIndex, childIndex, age) => {
    const newRooms = [...rooms];
    if (!newRooms[roomIndex].childAges) {
      newRooms[roomIndex].childAges = [];
    }
    newRooms[roomIndex].childAges[childIndex] = parseInt(age);
    onRoomsChange(newRooms);
  };

  return (
    <div className="relative" style={{ zIndex: 1000 }}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <input
          ref={guestInputRef}
          type="text"
          value={getGuestSummary()}
          onClick={() => setShowPopup(true)}
          readOnly
          className="w-full pl-10 pr-10 py-3 text-sm border border-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-400 transition-colors duration-200 shadow-sm"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {showPopup && (
        <div className="guest-selector-popup fixed bg-white border border-gray-200 rounded-lg shadow-lg p-4"
             style={{ 
               zIndex: 9999,
               top: guestInputRef.current ? guestInputRef.current.getBoundingClientRect().bottom + 8 : 'auto',
               left: guestInputRef.current ? guestInputRef.current.getBoundingClientRect().left : 'auto',
               width: guestInputRef.current ? Math.max(guestInputRef.current.getBoundingClientRect().width, 320) : '320px',
               maxHeight: '80vh',
               overflow: 'auto'
             }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Guests & Rooms
            </h3>
            <button
              type="button"
              onClick={() => setShowPopup(false)}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 mb-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-900">Rooms</div>
                <div className="text-xs text-gray-600">Maximum 6 rooms</div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => updateRoomCount(rooms.length - 1)}
                  className="w-8 h-8 bg-white border border-gray-300 rounded-full hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all duration-200"
                  disabled={rooms.length <= 1}
                >
                  <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <span className="w-8 text-center text-sm font-semibold text-gray-900">{rooms.length}</span>
                <button
                  type="button"
                  onClick={() => updateRoomCount(rooms.length + 1)}
                  className="w-8 h-8 bg-white border border-gray-300 rounded-full hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all duration-200"
                  disabled={rooms.length >= 6}
                >
                  <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3 max-h-48 overflow-y-auto">
            {rooms.map((room, roomIndex) => (
              <div key={roomIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Room {room.roomNumber}
                  </h4>
                  {rooms.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newRooms = rooms.filter((_, i) => i !== roomIndex);
                        newRooms.forEach((r, i) => r.roomNumber = i + 1);
                        onRoomsChange(newRooms);
                      }}
                      className="text-red-500 hover:text-red-700 p-1 rounded-lg hover:bg-red-50 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Adults</div>
                      <div className="text-xs text-gray-600">Age 13+</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => updateRoom(roomIndex, 'adults', room.adults - 1)}
                        className="w-6 h-6 bg-white border border-gray-300 rounded-full hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 shadow-sm transition-all duration-200"
                        disabled={room.adults <= 1}
                      >
                        <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <span className="w-6 text-center text-sm font-medium text-gray-900">{room.adults}</span>
                      <button
                        type="button"
                        onClick={() => updateRoom(roomIndex, 'adults', room.adults + 1)}
                        className="w-6 h-6 bg-white border border-gray-300 rounded-full hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 shadow-sm transition-all duration-200"
                        disabled={room.adults >= 8}
                      >
                        <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900">Children</div>
                      <div className="text-xs text-gray-600">Age 1-12</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => updateRoom(roomIndex, 'children', room.children - 1)}
                        className="w-6 h-6 bg-white border border-gray-300 rounded-full hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 shadow-sm transition-all duration-200"
                        disabled={room.children <= 0}
                      >
                        <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <span className="w-6 text-center text-sm font-medium text-gray-900">{room.children}</span>
                      <button
                        type="button"
                        onClick={() => updateRoom(roomIndex, 'children', room.children + 1)}
                        className="w-6 h-6 bg-white border border-gray-300 rounded-full hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 shadow-sm transition-all duration-200"
                        disabled={room.children >= 4}
                      >
                        <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                
                {room.children > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Children's Ages
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {Array.from({ length: room.children }, (_, childIndex) => (
                        <div key={childIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600 w-12">Child {childIndex + 1}:</span>
                          <select
                            value={room.childAges?.[childIndex] || 1}
                            onChange={(e) => updateChildAge(roomIndex, childIndex, e.target.value)}
                            className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
                          >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(age => (
                              <option key={age} value={age}>{age} year{age > 1 ? 's' : ''}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <button
            type="button"
            onClick={() => setShowPopup(false)}
            className="w-full mt-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            Apply Selection
          </button>
        </div>
      )}

      {showPopup && (
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

export default GuestSelector;